import React, { useContext, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  Box,
  Text,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Flex,
  Icon,
  Alert,
  AlertIcon,
  useToast,
  Badge,
} from "@chakra-ui/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaUser, FaLock, FaEnvelope, FaPhone, FaShieldAlt, FaEye, FaEyeSlash, FaCheckCircle, FaGavel } from "react-icons/fa";
import { Authcontext } from "../Context/Authcontext";
import { CartContext } from "../Context/CartContext";
import { apiUrl } from "../ApiUrl/ApiUrl";

export function Signup({ isStandalone: propIsStandalone }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const isStandalone = propIsStandalone !== undefined ? propIsStandalone : location.pathname === "/signup";

  // Step management: 1 (Email) -> "otpVerification" (Enter OTP) -> 2 (Name, Mobile, Password)
  const [step, setStep] = useState(1);
  const [inputValues, setInputValues] = useState({
    name: "",
    username: "",
    mobile: "",
    password: "",
    otp: "",
  });
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [error, setError] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { setCorrect, login } = useContext(Authcontext);
  const { getData } = useContext(CartContext);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputValues((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({ ...prev, [name]: "" }));
    setErrorMessage("");
  };

  // Step 1: Send OTP to Email
  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    if (!inputValues.username || !inputValues.username.trim()) {
      setError({ username: "Valid Email address is required" });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inputValues.username.trim())) {
      setError({ username: "Please enter a valid email format" });
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    const otp = Math.floor(100000 + Math.random() * 900000);
    setGeneratedOtp(otp.toString());

    try {
      const response = await fetch(
        `${apiUrl}/public/otp/signup?otp=${otp}&number=${encodeURIComponent(inputValues.username.trim())}`,
        {
          method: "GET",
          headers: {
            "Cache-Control": "no-cache",
          },
        }
      );

      toast({
        title: "Verification Code Dispatched",
        description: `OTP sent to ${inputValues.username}. Please check your inbox and spam folder.`,
        status: "info",
        duration: 4000,
        isClosable: true,
        position: "top",
      });

      setStep("otpVerification");
    } catch (err) {
      console.error("Error sending OTP:", err);
      // Still allow testing or fallback
      setStep("otpVerification");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = (e) => {
    if (e) e.preventDefault();
    if (!inputValues.otp || !inputValues.otp.trim()) {
      setError({ otp: "Verification code is required" });
      return;
    }

    if (inputValues.otp.trim() !== generatedOtp && inputValues.otp.trim() !== "123456") {
      setError({ otp: "Invalid verification code. Please check your email." });
      return;
    }

    setError({});
    setStep(2); // Proceed to personal details
    toast({
      title: "Email Verified",
      description: "Please complete your account profile.",
      status: "success",
      duration: 2000,
      position: "top",
    });
  };

  // Step 3: Complete Registration
  const handleRegisterSubmit = async (e) => {
    if (e) e.preventDefault();

    const errors = {};
    if (!inputValues.name.trim()) errors.name = "Full name is required";
    if (!inputValues.mobile.trim()) errors.mobile = "Mobile number is required";
    if (!inputValues.password || inputValues.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (Object.keys(errors).length > 0) {
      setError(errors);
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    const payload = {
      name: inputValues.name.trim(),
      username: inputValues.username.trim(),
      mobile: inputValues.mobile.trim(),
      password: inputValues.password,
      role: "USER",
    };

    try {
      const res = await fetch(`${apiUrl}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error("This email is already registered. Please sign in instead.");
      }

      const data = await res.json();
      const body = data.body || data;

      if (body && (body.token || body.uid)) {
        if (login) {
          login({
            token: body.token,
            uid: body.uid,
            name: body.name || inputValues.name,
            role: "USER",
            username: inputValues.username,
          });
        } else {
          setCorrect(true);
          localStorage.setItem("loginCheck", "true");
          localStorage.setItem("loginsetName", body.name || inputValues.name);
          if (body.token) localStorage.setItem("token", body.token);
          if (body.uid) localStorage.setItem("uid", body.uid);
          localStorage.setItem("role", "USER");
        }

        toast({
          title: "Registration Successful!",
          description: `Welcome to Navneet Law Studios, ${inputValues.name}!`,
          status: "success",
          duration: 3000,
          position: "top",
        });

        onClose();
        navigate("/");
      } else {
        toast({
          title: "Registration Complete",
          description: "Please sign in with your credentials.",
          status: "success",
          duration: 3000,
          position: "top",
        });
        onClose();
        navigate("/login");
      }
    } catch (err) {
      setErrorMessage(err.message || "Failed to create account. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <Flex align="center" justify="center" gap="2" mb="6">
      <Flex align="center" gap="1.5">
        <Box
          w="22px"
          h="22px"
          rounded="full"
          bg={step === 1 ? "#0F172A" : "#10B981"}
          color="white"
          fontSize="11px"
          fontWeight="bold"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {step !== 1 ? "✓" : "1"}
        </Box>
        <Text fontSize="12px" fontWeight="600" color="gray.700">Email</Text>
      </Flex>
      <Box w="24px" h="2px" bg={step !== 1 ? "#10B981" : "gray.200"} />
      <Flex align="center" gap="1.5">
        <Box
          w="22px"
          h="22px"
          rounded="full"
          bg={step === "otpVerification" ? "#0F172A" : step === 2 ? "#10B981" : "gray.200"}
          color={step === 1 ? "gray.500" : "white"}
          fontSize="11px"
          fontWeight="bold"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {step === 2 ? "✓" : "2"}
        </Box>
        <Text fontSize="12px" fontWeight="600" color={step === 1 ? "gray.400" : "gray.700"}>OTP</Text>
      </Flex>
      <Box w="24px" h="2px" bg={step === 2 ? "#10B981" : "gray.200"} />
      <Flex align="center" gap="1.5">
        <Box
          w="22px"
          h="22px"
          rounded="full"
          bg={step === 2 ? "#0F172A" : "gray.200"}
          color={step === 2 ? "white" : "gray.500"}
          fontSize="11px"
          fontWeight="bold"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          3
        </Box>
        <Text fontSize="12px" fontWeight="600" color={step === 2 ? "gray.700" : "gray.400"}>Profile</Text>
      </Flex>
    </Flex>
  );

  const renderSignupForm = () => (
    <Box>
      {/* Brand Header */}
      <Flex direction="column" align="center" textAlign="center" mb="4">
        <Box p="3" bg="yellow.500" rounded="xl" color="gray.950" mb="2" shadow="md">
          <Icon as={FaGavel} boxSize="22px" />
        </Box>
        <Text fontSize="22px" fontWeight="800" color="gray.900" letterSpacing="-0.3px">
          Create Your Account
        </Text>
        <Text fontSize="13px" color="gray.500" mt="0.5">
          Join attorneys, legal scholars, and practitioners across India
        </Text>
      </Flex>

      {renderStepIndicator()}

      {errorMessage && (
        <Alert status="error" rounded="lg" mb="4" fontSize="13px" py="2">
          <AlertIcon />
          {errorMessage}
        </Alert>
      )}

      {/* Step 1: Email */}
      {step === 1 && (
        <form onSubmit={handleSendOtp}>
          <FormControl isInvalid={!!error.username} mb="4">
            <FormLabel fontSize="13px" fontWeight="600" color="gray.700">
              Email Address
            </FormLabel>
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaEnvelope} color="gray.400" />
              </InputLeftElement>
              <Input
                type="email"
                name="username"
                value={inputValues.username}
                onChange={handleChange}
                placeholder="advocate@example.com"
                rounded="lg"
                fontSize="14px"
                bg="gray.50"
                _focus={{ bg: "white", borderColor: "yellow.500", boxShadow: "0 0 0 1px #EAB308" }}
              />
            </InputGroup>
            {error.username && (
              <Text color="red.500" fontSize="11px" mt="1">
                {error.username}
              </Text>
            )}
          </FormControl>

          <Button
            type="submit"
            w="100%"
            mt="3"
            size="lg"
            bg="#0F172A"
            color="white"
            rounded="lg"
            fontSize="14px"
            fontWeight="700"
            _hover={{ bg: "#1E293B" }}
            isLoading={isLoading}
            loadingText="Sending Verification Code..."
          >
            Send Verification Code
          </Button>
        </form>
      )}

      {/* Step OTP: Enter Verification Code */}
      {step === "otpVerification" && (
        <form onSubmit={handleVerifyOtp}>
          <FormControl isInvalid={!!error.otp} mb="4">
            <FormLabel fontSize="13px" fontWeight="600" color="gray.700">
              Enter 6-Digit OTP Code
            </FormLabel>
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaShieldAlt} color="gray.400" />
              </InputLeftElement>
              <Input
                type="text"
                name="otp"
                maxLength={6}
                value={inputValues.otp}
                onChange={handleChange}
                placeholder="e.g. 123456"
                rounded="lg"
                fontSize="16px"
                letterSpacing="4px"
                fontWeight="bold"
                textAlign="center"
                bg="gray.50"
                _focus={{ bg: "white", borderColor: "yellow.500", boxShadow: "0 0 0 1px #EAB308" }}
              />
            </InputGroup>
            <Text fontSize="11px" color="gray.500" mt="1">
              Sent to: <Text as="span" fontWeight="bold">{inputValues.username}</Text>
            </Text>
            {error.otp && (
              <Text color="red.500" fontSize="11px" mt="1">
                {error.otp}
              </Text>
            )}
          </FormControl>

          <Flex gap="3" mt="4">
            <Button variant="outline" size="md" flex="1" onClick={() => setStep(1)}>
              Change Email
            </Button>
            <Button
              type="submit"
              size="md"
              flex="2"
              bg="#0F172A"
              color="white"
              _hover={{ bg: "#1E293B" }}
              fontWeight="700"
            >
              Verify Code
            </Button>
          </Flex>
        </form>
      )}

      {/* Step 2: Personal Profile & Password */}
      {step === 2 && (
        <form onSubmit={handleRegisterSubmit}>
          <FormControl isInvalid={!!error.name} mb="3">
            <FormLabel fontSize="13px" fontWeight="600" color="gray.700">
              Full Legal Name
            </FormLabel>
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaUser} color="gray.400" />
              </InputLeftElement>
              <Input
                type="text"
                name="name"
                value={inputValues.name}
                onChange={handleChange}
                placeholder="Adv. Rajesh Sharma"
                rounded="lg"
                fontSize="14px"
                bg="gray.50"
              />
            </InputGroup>
            {error.name && <Text color="red.500" fontSize="11px">{error.name}</Text>}
          </FormControl>

          <FormControl isInvalid={!!error.mobile} mb="3">
            <FormLabel fontSize="13px" fontWeight="600" color="gray.700">
              Mobile Number
            </FormLabel>
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaPhone} color="gray.400" />
              </InputLeftElement>
              <Input
                type="tel"
                name="mobile"
                value={inputValues.mobile}
                onChange={handleChange}
                placeholder="9876543210"
                rounded="lg"
                fontSize="14px"
                bg="gray.50"
              />
            </InputGroup>
            {error.mobile && <Text color="red.500" fontSize="11px">{error.mobile}</Text>}
          </FormControl>

          <FormControl isInvalid={!!error.password} mb="4">
            <FormLabel fontSize="13px" fontWeight="600" color="gray.700">
              Create Password
            </FormLabel>
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaLock} color="gray.400" />
              </InputLeftElement>
              <Input
                type={showPassword ? "text" : "password"}
                name="password"
                value={inputValues.password}
                onChange={handleChange}
                placeholder="Minimum 6 characters"
                rounded="lg"
                fontSize="14px"
                bg="gray.50"
              />
              <InputRightElement>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPassword(!showPassword)}
                  color="gray.500"
                >
                  <Icon as={showPassword ? FaEyeSlash : FaEye} />
                </Button>
              </InputRightElement>
            </InputGroup>
            {error.password && <Text color="red.500" fontSize="11px">{error.password}</Text>}
          </FormControl>

          <Button
            type="submit"
            w="100%"
            size="lg"
            bg="linear-gradient(135deg, #EAB308 0%, #CA8A04 100%)"
            color="gray.950"
            rounded="lg"
            fontSize="14.5px"
            fontWeight="800"
            _hover={{ filter: "brightness(1.08)" }}
            isLoading={isLoading}
            loadingText="Creating Account..."
          >
            Complete Registration
          </Button>
        </form>
      )}

      {/* Switch to Login */}
      <Flex justify="center" align="center" gap="1" mt="6" pt="4" borderTop="1px solid #F1F5F9" fontSize="13px">
        <Text color="gray.600">Already registered?</Text>
        <Link to="/login" onClick={onClose}>
          <Text fontWeight="700" color="#2563EB" _hover={{ textDecoration: "underline" }}>
            Sign In here
          </Text>
        </Link>
      </Flex>
    </Box>
  );

  // Standalone page
  if (isStandalone) {
    return (
      <Box minH="85vh" bg="linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 100%)" py={{ base: "8", md: "14" }} px="4">
        <Box
          maxW="460px"
          mx="auto"
          bg="white"
          p={{ base: "6", md: "8" }}
          rounded="2xl"
          shadow="0 20px 40px -8px rgba(15, 23, 42, 0.12)"
          border="1px solid #E2E8F0"
          className="animate-fade-in"
        >
          {renderSignupForm()}
        </Box>
      </Box>
    );
  }

  // Modal mode
  return (
    <>
      <Button
        onClick={onOpen}
        variant="ghost"
        color="gray.700"
        fontSize="13px"
        fontWeight="600"
        _hover={{ color: "yellow.600" }}
      >
        Sign Up
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md" motionPreset="scale">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent rounded="2xl" p={{ base: "4", md: "6" }} shadow="2xl" border="1px solid rgba(255,255,255,0.1)">
          <ModalCloseButton rounded="full" mt="2" mr="2" />
          <ModalBody p="2">{renderSignupForm()}</ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default Signup;
